'use client';
import { useMemo, useState, useCallback } from 'react';
import { compositeScore } from '@/lib/composite-score';
import { useResource } from '@/lib/use-resource';
import { TierStockList } from './scatter3d/TierStockList';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import { EarningsCalendar } from './EarningsCalendar';
import { ScoredStock } from '@/lib/compute';
import { getSectorColor } from '@/components/ControlPanel';
import { SpinningPlanet } from './SpinningPlanet';
import { buildStocks3D as buildStocks3DPure } from '@/lib/scatter3d-geometry';
import { formatHistoricalDate } from '@/lib/scatter3d-time';
import { ScatterControlsOverlay } from '@/components/scatter3d/ScatterControlsOverlay';
import { ScatterLegend } from '@/components/scatter3d/ScatterLegend';
import { StockPoint } from './scatter3d/StockPoint';
import { SphereGrid, CameraSetup } from './scatter3d/Scene';
import { shortName, type ScatterPlot3DProps as Props, type TechnicalStock } from './scatter3d/types';

// Local helper that ignores the legacy technical-data params
// (kept on the public Props for backwards compat but no longer drive Z position).
const buildStocks3D = (stocks: ScoredStock[], ..._unused: TechnicalStock[][]) =>
  buildStocks3DPure(stocks, getSectorColor);

export function ScatterPlot3D({
  stocks, totalCount, technicalData, technicalWeeklyData, technicalMonthlyData,
  highlightSymbol, onSelectStock, selectedSymbol,
  enteredAlphaSymbols, exitedAlphaSymbols,
  historicalPeriod, historicalDate, historicalLoading, onHistoricalPeriodChange,
  updatedAt, portfolioIsolated,
  projectionHorizon, onProjectionChange,
  factorHighlightStocks = new Set(),
  sectorTopComposite = new Set(),
  alphaZoneStocks = new Set(),
  hideEarningsCalendar = false,
  vMode,
  onVModeChange,
  legendPosition = 'top-left',
  tierButtons,
  onTierFilterChange,
}: Props) {
  const [tierFilter, setTierFilterRaw] = useState<'all' | 'alpha' | 'top10q' | 'top20q' | 'top15q' | 'combined'>('all');
  const setTierFilter = (next: 'all' | 'alpha' | 'top10q' | 'top20q' | 'top15q' | 'combined') => {
    setTierFilterRaw(next);
    onTierFilterChange?.(next);
  };
  const [signalFilter, setSignalFilter] = useState<'all' | 'enteredAlpha' | 'exitedAlpha'>('all');
  // Portfolio weights for the weighted-avg composite total at the bottom of
  // the isolated portfolio table. Shared via useResource cache.
  const { data: portfolioData } = useResource<{ weights?: Record<string, number> }>('/api/portfolio');
  const portfolioWeights = portfolioData?.weights ?? {};

  const compositeOf = (q: number, v: number): number => compositeScore(q, v) ?? 0;

  const stocks3d = useMemo(
    () => buildStocks3D(stocks, technicalData, technicalWeeklyData, technicalMonthlyData),
    [stocks, technicalData, technicalWeeklyData, technicalMonthlyData],
  );

  const handleClick = useCallback((symbol: string) => {
    onSelectStock(symbol);
  }, [onSelectStock]);

  const searchLower = highlightSymbol.toLowerCase();

  const tierPicks = useMemo(() => {
    if (tierFilter === 'all') return new Set<string>();
    if (tierFilter === 'alpha') {
      // Alpha Zone: canonical basket (legacy filter, kept for backwards compat).
      return alphaZoneStocks;
    }
    const topN = tierFilter === 'top10q' ? 10 : tierFilter === 'top20q' ? 20 : tierFilter === 'top15q' ? 15 : 0;
    if (topN > 0) {
      return new Set(
        [...stocks3d]
          .sort((a, b) => b.quality - a.quality)
          .slice(0, topN)
          .map(s => s.symbol)
      );
    }
    return new Set<string>();
  }, [stocks3d, tierFilter, alphaZoneStocks]);

  if (stocks3d.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center" style={{ background: '#0a0c10' }}>
        <div className="text-[#94a3b8] text-sm">
          No stocks with all 3 scores available. Run <code className="text-blue-400">npm run fetch-technical</code> first.
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 relative" style={{ background: '#0a0c10' }}>
      {/* Earnings Calendar — hidden in basket-only embeds (alpha-zone visualizer) */}
      {!hideEarningsCalendar && (
        <EarningsCalendar onSelectSymbol={onSelectStock} selectedSymbol={selectedSymbol} />
      )}

      {/* Last refresh + next refresh */}
      {updatedAt && (
        <div className="absolute bottom-3 right-3 z-20 font-mono text-[10px] text-right" style={{ color: '#4a5568' }}>
          {new Date(updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}{' '}
          {new Date(updatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
          {' · '}Next 6:00 AM
        </div>
      )}
      <ScatterControlsOverlay
        historicalMonth={historicalPeriod ?? null}
        historicalLoading={historicalLoading ?? false}
        onHistoricalMonthChange={onHistoricalPeriodChange}
        tierFilter={tierFilter}
        onTierFilterChange={setTierFilter}
        projectionHorizon={projectionHorizon}
        onProjectionChange={onProjectionChange}
        vMode={vMode}
        onVModeChange={onVModeChange}
        tierButtons={tierButtons}
      />

      {/* Historical period banner */}
      {historicalPeriod && historicalDate && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
          <div className="px-4 py-1.5 rounded-lg text-xs font-mono" style={{
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.25)',
            color: '#f59e0b',
          }}>
            Viewing: {formatHistoricalDate(historicalDate)}
          </div>
        </div>
      )}
      {historicalLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, rgba(10,12,16,0.55), rgba(10,12,16,0.85))' }}>
          <SpinningPlanet label="Loading snapshot…" sublabel="Pulling historical factor scores" />
        </div>
      )}
      {historicalPeriod && !historicalDate && !historicalLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none"
          style={{ background: 'radial-gradient(ellipse at center, rgba(10,12,16,0.55), rgba(10,12,16,0.85))' }}>
          <SpinningPlanet label="No snapshot available" sublabel="No historical data for this period" />
        </div>
      )}

      {/* Projection watermark */}
      {projectionHorizon && projectionHorizon !== 'now' && !historicalPeriod && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10">
          <div className="px-4 py-1.5 rounded-lg text-xs font-mono" style={{
            background: 'rgba(168, 85, 247, 0.1)',
            border: '1px solid rgba(168, 85, 247, 0.25)',
            color: '#a855f7',
          }}>
            Projected {projectionHorizon} Forward — Valuation rescored using consensus estimates
          </div>
        </div>
      )}

      <ScatterLegend
        signalFilter={signalFilter}
        onSignalFilterChange={setSignalFilter}
        enteredCount={enteredAlphaSymbols?.size ?? 0}
        exitedCount={exitedAlphaSymbols?.size ?? 0}
        position={legendPosition}
      />

      <TierStockList
        tierFilter={tierFilter}
        stocks3d={stocks3d}
        tierPicks={tierPicks}
        selectedSymbol={selectedSymbol}
        onSelectStock={handleClick}
      />

      {/* Portfolio table when isolated */}
      {portfolioIsolated && stocks3d.length > 0 && stocks3d.length <= 50 && (() => {
        // Compute composite per stock (canonical compositeScore: α=1.25/β=0.90/shift=0.5) + weighted-avg over the
        // user's actual weights. Renormalize so the avg is over only the names
        // currently rendered (in case some symbols are missing from weights).
        const rows = stocks3d.map(s => ({ ...s, composite: compositeOf(s.quality, s.valuation), weight: portfolioWeights[s.symbol] ?? 0 }));
        const sumW = rows.reduce((a, r) => a + r.weight, 0);
        // Fall back to equal-weight if no weights provided (so the total still renders sensibly)
        const useWeights = sumW > 0;
        const totalComp = useWeights
          ? rows.reduce((a, r) => a + (r.weight / sumW) * r.composite, 0)
          : rows.reduce((a, r) => a + r.composite, 0) / rows.length;
        const totalQ = useWeights
          ? rows.reduce((a, r) => a + (r.weight / sumW) * r.quality, 0)
          : rows.reduce((a, r) => a + r.quality, 0) / rows.length;
        const totalV = useWeights
          ? rows.reduce((a, r) => a + (r.weight / sumW) * r.valuation, 0)
          : rows.reduce((a, r) => a + r.valuation, 0) / rows.length;
        const totalM = useWeights
          ? rows.reduce((a, r) => a + (r.weight / sumW) * r.technical, 0)
          : rows.reduce((a, r) => a + r.technical, 0) / rows.length;
        return (
        <div
          className="absolute bottom-10 left-3 z-10 font-mono text-[10px] rounded-lg overflow-hidden"
          style={{
            background: 'rgba(10, 12, 20, 0.92)',
            border: '1px solid #f59e0b30',
            maxHeight: 400,
            overflowY: 'auto',
            minWidth: 280,
          }}
        >
          <div className="px-3 py-2 font-semibold text-[11px] sticky top-0" style={{
            background: 'rgba(10, 12, 20, 0.98)',
            color: '#f59e0b',
            borderBottom: '1px solid #1e293b',
          }}>
            <div className="flex items-center justify-between gap-2">
              <span>Portfolio ({stocks3d.length})</span>
              <span className="flex gap-2 text-[9px] text-[#64748b] font-normal">
                <span style={{ width: 32, textAlign: 'right', color: '#f59e0b' }}>Comp</span>
                <span style={{ width: 22, textAlign: 'right' }}>Q</span>
                <span style={{ width: 22, textAlign: 'right' }}>V</span>
                <span style={{ width: 22, textAlign: 'right' }}>M</span>
              </span>
            </div>
          </div>
          {[...rows]
            .sort((a, b) => b.composite - a.composite)
            .map(s => (
              <div
                key={s.symbol}
                className="px-3 py-1 flex items-center gap-2 cursor-pointer hover:bg-white/5"
                onClick={() => handleClick(s.symbol)}
                style={{
                  borderBottom: '1px solid #0f1525',
                  background: selectedSymbol === s.symbol ? 'rgba(255,255,255,0.06)' : undefined,
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                <span className="text-white font-semibold" style={{ minWidth: 38 }}>{s.symbol}</span>
                <span className="text-[#64748b] truncate" style={{ width: 70 }}>{shortName(s.name)}</span>
                <span className="text-[8px] flex-shrink-0 truncate" style={{ width: 60, color: s.color, opacity: 0.7 }}>{s.sector}</span>
                <span className="flex gap-2 text-[9px] flex-shrink-0 ml-auto">
                  <span
                    style={{
                      width: 32,
                      textAlign: 'right',
                      fontWeight: 700,
                      color: s.composite >= 3 ? '#4ade80'
                        : s.composite >= 2 ? '#fcd34d'
                        : s.composite >= 1 ? '#f59e0b'
                        : s.composite >= 0 ? '#94a3b8'
                        : '#f87171',
                    }}
                  >
                    {s.composite.toFixed(2)}
                  </span>
                  <span style={{ width: 22, textAlign: 'right', color: s.quality > 0 ? '#4ade80' : '#f87171' }}>{s.quality.toFixed(1)}</span>
                  <span style={{ width: 22, textAlign: 'right', color: s.valuation > 0 ? '#4ade80' : '#f87171' }}>{s.valuation.toFixed(1)}</span>
                  <span style={{ width: 22, textAlign: 'right', color: s.technical > 0 ? '#4ade80' : '#f87171' }}>{s.technical.toFixed(1)}</span>
                </span>
              </div>
            ))}
          {/* Weighted-average portfolio total */}
          <div
            className="px-3 py-1.5 flex items-center gap-2 sticky bottom-0"
            style={{
              borderTop: '2px solid #f59e0b50',
              background: 'rgba(10, 12, 20, 0.98)',
            }}
          >
            <span className="w-1.5 h-1.5 flex-shrink-0" />
            <span className="font-bold" style={{ color: '#f59e0b', minWidth: 38 }}>TOTAL</span>
            <span className="text-[#64748b] truncate text-[9px]" style={{ width: 70 }}>
              {useWeights ? 'wtd avg' : 'equal-wtd'}
            </span>
            <span style={{ width: 60 }} />
            <span className="flex gap-2 text-[9px] flex-shrink-0 ml-auto font-bold">
              <span
                style={{
                  width: 32,
                  textAlign: 'right',
                  color: totalComp >= 3 ? '#4ade80'
                    : totalComp >= 2 ? '#fcd34d'
                    : totalComp >= 1 ? '#f59e0b'
                    : totalComp >= 0 ? '#94a3b8'
                    : '#f87171',
                }}
              >
                {totalComp.toFixed(2)}
              </span>
              <span style={{ width: 22, textAlign: 'right', color: totalQ > 0 ? '#4ade80' : '#f87171' }}>{totalQ.toFixed(1)}</span>
              <span style={{ width: 22, textAlign: 'right', color: totalV > 0 ? '#4ade80' : '#f87171' }}>{totalV.toFixed(1)}</span>
              <span style={{ width: 22, textAlign: 'right', color: totalM > 0 ? '#4ade80' : '#f87171' }}>{totalM.toFixed(1)}</span>
            </span>
          </div>
        </div>
        );
      })()}

      {/* Stats */}
      <div className="absolute bottom-3 left-3 z-10 text-xs font-mono" style={{ color: '#64748b' }}>
        {totalCount ?? stocks.length} stocks &middot; drag to rotate &middot; scroll to zoom
      </div>

      <Canvas
        gl={{ antialias: true, alpha: false }}
        style={{ background: '#0a0c10' }}
        onPointerMissed={() => {
          onSelectStock('');
        }}
      >
        <CameraSetup />
        <OrbitControls
          enableDamping
          dampingFactor={0.05}
          minDistance={4}
          maxDistance={35}
        />

        <ambientLight intensity={0.4} />
        <directionalLight position={[10, 10, 5]} intensity={1} />
        <directionalLight position={[-5, -5, -5]} intensity={0.4} />
        <pointLight position={[0, 0, 0]} intensity={0.8} distance={20} color="#e2e8f0" />

        <SphereGrid />

        {/* Green glow around tier picks — only visible when tier filter is active */}

        {/* Alpha zone — minimum bounding ellipsoid, visible only when Alpha filter is active */}
        {tierFilter === 'alpha' && alphaZoneStocks.size > 0 && (() => {
          // Alpha Zone: top 20 by composite (Q≥0), passed in from page.tsx.
          const pts = stocks3d.filter(s => alphaZoneStocks.has(s.symbol));
          if (pts.length < 2) return null;

          // Minimum bounding ellipsoid via iterative Khachiyan-style approach
          // Center = midpoint of bounding box, then expand radii until all points are inside
          const minX = Math.min(...pts.map(s => s.x));
          const maxX = Math.max(...pts.map(s => s.x));
          const minY = Math.min(...pts.map(s => s.y));
          const maxY = Math.max(...pts.map(s => s.y));
          const minZ = Math.min(...pts.map(s => s.z));
          const maxZ = Math.max(...pts.map(s => s.z));

          // Center of bounding box
          const cx = (minX + maxX) / 2;
          const cy = (minY + maxY) / 2;
          const cz = (minZ + maxZ) / 2;

          // Half-extents from center to edges + buffer for planet radius
          const buf = 0.5;
          let rx = (maxX - minX) / 2 + buf;
          let ry = (maxY - minY) / 2 + buf;
          let rz = (maxZ - minZ) / 2 + buf;

          // Ensure minimum radius on any axis so it doesn't collapse to a disc
          rx = Math.max(rx, 0.8);
          ry = Math.max(ry, 0.8);
          rz = Math.max(rz, 0.8);

          // Verify all points are inside, expand if needed
          for (const p of pts) {
            const d = ((p.x - cx) / rx) ** 2 + ((p.y - cy) / ry) ** 2 + ((p.z - cz) / rz) ** 2;
            if (d > 1) {
              // Point outside — scale up uniformly
              const scale = Math.sqrt(d) * 1.05;
              rx *= scale;
              ry *= scale;
              rz *= scale;
            }
          }

          return (
            <group position={[cx, cy, cz]}>
              <mesh scale={[rx, ry, rz]}>
                <sphereGeometry args={[1, 32, 32]} />
                <meshBasicMaterial
                  color="#22c55e"
                  transparent
                  opacity={0.03}
                  side={THREE.BackSide}
                  depthWrite={false}
                />
              </mesh>
              <mesh scale={[rx, ry, rz]}>
                <sphereGeometry args={[1, 16, 16]} />
                <meshBasicMaterial
                  color="#22c55e"
                  transparent
                  opacity={0.06}
                  wireframe
                  depthWrite={false}
                />
              </mesh>
            </group>
          );
        })()}

        {stocks3d.map(s => {
          const isFactorTop = factorHighlightStocks.size > 0 && factorHighlightStocks.has(s.symbol);
          const isAlpha = alphaZoneStocks.has(s.symbol);
          const isSectorTop = sectorTopComposite.has(s.symbol);
          return (
          <StockPoint
            key={s.symbol}
            stock={s}
            isSelected={selectedSymbol === s.symbol}
            isHighlighted={isFactorTop || (searchLower.length > 0 && (s.symbol.toLowerCase().includes(searchLower) || s.name.toLowerCase().includes(searchLower)))}
            isSectorTop={isSectorTop}
            onClick={() => handleClick(s.symbol)}
            isAlphaZone={isAlpha}
            dimmed={
              (factorHighlightStocks.size > 0 && !isFactorTop) ||
              (!!selectedSymbol && selectedSymbol !== s.symbol) ||
              (searchLower.length > 0 && !s.symbol.toLowerCase().includes(searchLower) && !s.name.toLowerCase().includes(searchLower)) ||
              // 'combined' is the Portfolio Visualizer's super-planet mode —
              // the parent has already collapsed plotStocks to one synthetic
              // position, so no tier-based dimming is appropriate (an empty
              // tierPicks would otherwise dim the lone planet away).
              (tierFilter !== 'all' && tierFilter !== 'combined' && !tierPicks.has(s.symbol)) ||
              (signalFilter === 'enteredAlpha' && !enteredAlphaSymbols?.has(s.symbol)) ||
              (signalFilter === 'exitedAlpha' && !exitedAlphaSymbols?.has(s.symbol))
            }
            signal={
              enteredAlphaSymbols?.has(s.symbol) ? 'enteredAlpha'
              : exitedAlphaSymbols?.has(s.symbol) ? 'exitedAlpha'
              : null
            }
          />
        )})}
        
      </Canvas>
    </div>
  );
}
